/**
 * Feb 2026 — Admin → Reports → Post-Test Service Revenue.
 *
 * PWS Noosa / PWS Maroochydore only. Preview/development only per the
 * build brief — the live data sync below only ever performs read-only
 * GETs against the existing ServiceM8 connection; nothing here writes
 * back to ServiceM8 or alters any customer-facing flow.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Download, DollarSign, AlertTriangle, Loader2,
  PlusCircle, Trash2, CheckCircle2, XCircle, FlaskConical, History, UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const REGION_LABELS = {
  pws_noosa: "PWS Noosa",
  pws_maroochydore: "PWS Maroochydore",
  excluded: "Excluded",
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmtBrisbaneDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Brisbane", day: "2-digit", month: "short", year: "numeric" }).format(new Date(iso));
  } catch (e) {
    return "—";
  }
};
const fmtBrisbaneDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Brisbane", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  } catch (e) {
    return "—";
  }
};
const REVIEW_REASON_LABELS = {
  unresolved_region: "Region not confirmed",
  missing_address: "No job address on file",
  missing_completion_time: "No completion time recorded",
  ambiguous_property_match: "Ambiguous property match",
};

export default function AdminPWSSM8Revenue() {
  const [includeTest, setIncludeTest] = useState(false);
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newEntry, setNewEntry] = useState({ match_type: "postcode", label: "", region: "pws_noosa", notes: "" });
  const [bulkImport, setBulkImport] = useState({ match_type: "postcode", raw_text: "", region: "pws_noosa", notes: "" });
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, reg, rq, al] = await Promise.all([
        api.get("/admin/pws-sm8-revenue/summary", { params: { include_test: includeTest } }),
        api.get("/admin/pws-sm8-revenue/reports", { params: { include_test: includeTest } }),
        api.get("/admin/pws-sm8-revenue/registry"),
        api.get("/admin/pws-sm8-revenue/review-queue", { params: { include_test: includeTest } }),
        api.get("/admin/pws-sm8-revenue/audit-log"),
      ]);
      setSummary(s.data);
      setReports(r.data.rows || []);
      setRegistry(reg.data.rows || []);
      setReviewQueue(rq.data.rows || []);
      setAuditLog(al.data.rows || []);
    } catch (e) {
      toast.error("Failed to load Post-Test Service Revenue data");
    } finally {
      setLoading(false);
    }
  }, [includeTest]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runAction = async (label, fn) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(label);
      await loadAll();
      return res;
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await api.get("/admin/pws-sm8-revenue/reports.csv", {
        params: { include_test: includeTest }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "post_test_service_revenue.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pws-sm8-revenue/sync", {});
      if (data.already_running) {
        toast.info("A sync is already running — checking progress…");
      } else {
        toast.success("Sync started — this runs in the background and can take a few minutes for a full history pull");
      }
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const { data: status } = await api.get("/admin/pws-sm8-revenue/sync/status");
          if (status.status === "done") {
            clearInterval(pollRef.current);
            setBusy(false);
            toast.success(`Sync complete — ${status.jobs_cached} jobs cached`);
            loadAll();
          } else if (status.status === "error") {
            clearInterval(pollRef.current);
            setBusy(false);
            toast.error(`Sync failed: ${status.error}`);
          } else if (tries > 180) {
            clearInterval(pollRef.current);
            setBusy(false);
            toast.info("Sync is still running in the background — check back shortly");
          }
        } catch (pollErr) {
          clearInterval(pollRef.current);
          setBusy(false);
          toast.error("Lost connection while checking sync progress");
        }
      }, 2000);
    } catch (e) {
      setBusy(false);
      toast.error(`Sync failed to start: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const addRegistryEntry = async () => {
    if (!newEntry.label.trim()) {
      toast.error("Enter a postcode or suburb first");
      return;
    }
    await runAction("Registry entry saved", () =>
      api.post("/admin/pws-sm8-revenue/registry", newEntry));
    setNewEntry({ match_type: "postcode", label: "", region: "pws_noosa", notes: "" });
  };

  const importBulkRegistry = async () => {
    if (!bulkImport.raw_text.trim()) {
      toast.error("Paste a list of postcodes or suburbs first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pws-sm8-revenue/registry/bulk", bulkImport);
      toast.success(`${data.total} entries imported — ${data.created} new, ${data.updated} updated`);
      setBulkImport((v) => ({ ...v, raw_text: "" }));
      await loadAll();
    } catch (e) {
      toast.error(`Bulk import failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const deleteRegistryEntry = async (id) => {
    await runAction("Registry entry removed", () =>
      api.delete(`/admin/pws-sm8-revenue/registry/${id}`));
  };

  const resolveReview = async (id, action, region) => {
    await runAction("Review item updated", () =>
      api.post(`/admin/pws-sm8-revenue/review-queue/${id}/resolve`, { action, region }));
  };

  return (
    <PortalShell title="Post-Test Service Revenue" subtitle="PWS Noosa / PWS Maroochydore — preview only">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8" data-testid="admin-pws-sm8-revenue-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin/attribution" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
               data-testid="pws-sm8-revenue-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Revenue
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Development / preview only — do not treat as live revenue
            </div>
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="pws-sm8-revenue-csv-btn">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" disabled={busy}
                   onClick={() => runAction("Re-run attribution", () =>
                     api.post("/admin/pws-sm8-revenue/reattribute"))}
                   data-testid="pws-sm8-revenue-reattribute-btn">
              <History className="w-4 h-4 mr-1" /> Re-run attribution (from local cache)
            </Button>
            <Button size="sm" disabled={busy} onClick={runSync} data-testid="pws-sm8-revenue-sync-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Sync from ServiceM8 (read-only)
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Switch id="include-test" checked={includeTest} onCheckedChange={setIncludeTest}
                 data-testid="pws-sm8-revenue-include-test-toggle" />
          <label htmlFor="include-test" className="text-slate-600">
            Include demo/test data (flagged <code>is_test</code>) in the numbers below
          </label>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400">Loading…</div>
        ) : (
          <>
            {/* KPI summary */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="pws-sm8-revenue-kpis">
              <KpiCard icon={DollarSign} label="Total attributed revenue (incl. GST)"
                      value={fmtMoney(summary?.total_revenue)} accent="emerald" />
              <KpiCard icon={FlaskConical} label="Total jobs (incl. pending invoice)" value={summary?.total_jobs ?? 0} />
              <KpiCard icon={CheckCircle2} label="Reports with attributed revenue"
                      value={summary?.reports_with_attributed_revenue ?? 0} />
              <KpiCard icon={AlertTriangle} label="Review queue (open)" value={summary?.review_queue_open ?? 0}
                      accent={summary?.review_queue_open ? "amber" : undefined} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {["pws_noosa", "pws_maroochydore"].map((region) => (
                <div key={region} className="rounded-xl border border-slate-200 p-4" data-testid={`pws-sm8-revenue-region-${region}`}>
                  <div className="text-sm font-semibold text-slate-700">{REGION_LABELS[region]}</div>
                  <div className="mt-2 text-2xl font-bold text-slate-900">
                    {fmtMoney(summary?.by_region?.[region]?.revenue)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {summary?.by_region?.[region]?.jobs ?? 0} jobs ·{" "}
                    {summary?.by_region?.[region]?.pending_invoice_jobs ?? 0} pending invoice
                  </div>
                </div>
              ))}
            </div>

            {/* Report detail drill-down */}
            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-3">Per-report breakdown</h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid="pws-sm8-revenue-reports-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Property</th>
                      <th className="text-left px-3 py-2">Region</th>
                      <th className="text-left px-3 py-2">Published</th>
                      <th className="text-left px-3 py-2">Window</th>
                      <th className="text-right px-3 py-2">Jobs</th>
                      <th className="text-right px-3 py-2">Pending invoice</th>
                      <th className="text-right px-3 py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No attributed reports yet</td></tr>
                    )}
                    {reports.map((r) => (
                      <tr key={r.passport_test_id} className="border-t border-slate-100" data-testid={`pws-sm8-revenue-report-row-${r.passport_test_id}`}>
                        <td className="px-3 py-2">{r.property_address || "—"}</td>
                        <td className="px-3 py-2">{REGION_LABELS[r.region] || r.region}</td>
                        <td className="px-3 py-2">{fmtBrisbaneDate(r.first_published_at)}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {fmtBrisbaneDate(r.window_start)} → {fmtBrisbaneDate(r.window_end)}
                        </td>
                        <td className="px-3 py-2 text-right">{r.job_count}</td>
                        <td className="px-3 py-2 text-right">{r.pending_invoice_job_count}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Region registry */}
            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">PWS Region Registry</h2>
              <p className="text-sm text-slate-500 mb-3">
                Admin-confirmed postcodes/suburbs only — never guessed. A job at an unrecognised
                address goes to the review queue below, it is never silently included or excluded.
              </p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="pws-sm8-revenue-registry-form">
                <Select value={newEntry.match_type} onValueChange={(v) => setNewEntry((e) => ({ ...e, match_type: v }))}>
                  <SelectTrigger className="w-32" data-testid="pws-sm8-revenue-registry-match-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="postcode">Postcode</SelectItem>
                    <SelectItem value="suburb">Suburb</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="e.g. 4566 or Noosaville" value={newEntry.label}
                      onChange={(e) => setNewEntry((v) => ({ ...v, label: e.target.value }))}
                      className="w-48" data-testid="pws-sm8-revenue-registry-label-input" />
                <Select value={newEntry.region} onValueChange={(v) => setNewEntry((e) => ({ ...e, region: v }))}>
                  <SelectTrigger className="w-44" data-testid="pws-sm8-revenue-registry-region-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pws_noosa">PWS Noosa</SelectItem>
                    <SelectItem value="pws_maroochydore">PWS Maroochydore</SelectItem>
                    <SelectItem value="excluded">Excluded (Gympie / Glasshouse / other)</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Notes (optional)" value={newEntry.notes}
                      onChange={(e) => setNewEntry((v) => ({ ...v, notes: e.target.value }))}
                      className="w-48" data-testid="pws-sm8-revenue-registry-notes-input" />
                <Button size="sm" onClick={addRegistryEntry} disabled={busy} data-testid="pws-sm8-revenue-registry-add-btn">
                  <PlusCircle className="w-4 h-4 mr-1" /> Add / update
                </Button>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 mb-4" data-testid="pws-sm8-revenue-registry-bulk-form">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1">
                  <UploadCloud className="w-4 h-4" /> Bulk import
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Paste a list of postcodes or suburbs — one per line, or separated by commas —
                  and assign them ALL to one region in one go. Existing entries get updated, new
                  ones get added. Nothing here is pre-filled or guessed — paste your own verified
                  list. (Use commas/new lines only — not spaces, since suburb names can contain one.)
                </p>
                <Textarea placeholder={"e.g.\n4566\n4567, 4573\n4575"} rows={3}
                         value={bulkImport.raw_text}
                         onChange={(e) => setBulkImport((v) => ({ ...v, raw_text: e.target.value }))}
                         className="mb-2 font-mono text-sm" data-testid="pws-sm8-revenue-registry-bulk-textarea" />
                <div className="flex flex-wrap gap-2 items-center">
                  <Select value={bulkImport.match_type} onValueChange={(v) => setBulkImport((e) => ({ ...e, match_type: v }))}>
                    <SelectTrigger className="w-32" data-testid="pws-sm8-revenue-registry-bulk-match-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="postcode">Postcode</SelectItem>
                      <SelectItem value="suburb">Suburb</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={bulkImport.region} onValueChange={(v) => setBulkImport((e) => ({ ...e, region: v }))}>
                    <SelectTrigger className="w-44" data-testid="pws-sm8-revenue-registry-bulk-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pws_noosa">PWS Noosa</SelectItem>
                      <SelectItem value="pws_maroochydore">PWS Maroochydore</SelectItem>
                      <SelectItem value="excluded">Excluded (Gympie / Glasshouse / other)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Notes (optional)" value={bulkImport.notes}
                        onChange={(e) => setBulkImport((v) => ({ ...v, notes: e.target.value }))}
                        className="w-48" data-testid="pws-sm8-revenue-registry-bulk-notes-input" />
                  <Button size="sm" onClick={importBulkRegistry} disabled={busy} data-testid="pws-sm8-revenue-registry-bulk-import-btn">
                    <UploadCloud className="w-4 h-4 mr-1" /> Import list
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid="pws-sm8-revenue-registry-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Value</th>
                      <th className="text-left px-3 py-2">Region</th>
                      <th className="text-left px-3 py-2">Notes</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {registry.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                        Empty — every real job will go to the review queue until you add entries here.
                      </td></tr>
                    )}
                    {registry.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100" data-testid={`pws-sm8-revenue-registry-row-${row.id}`}>
                        <td className="px-3 py-2 capitalize">{row.match_type}</td>
                        <td className="px-3 py-2 font-mono">{row.label}</td>
                        <td className="px-3 py-2">{REGION_LABELS[row.region] || row.region}</td>
                        <td className="px-3 py-2 text-slate-500">{row.notes}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => deleteRegistryEntry(row.id)} data-testid={`pws-sm8-revenue-registry-delete-${row.id}`}
                                 className="text-slate-400 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Review queue */}
            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-3">Review queue (open)</h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm" data-testid="pws-sm8-revenue-review-queue-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-left px-3 py-2">Job address</th>
                      <th className="text-left px-3 py-2">Completion</th>
                      <th className="text-right px-3 py-2">Invoice</th>
                      <th className="px-3 py-2">Resolve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewQueue.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Nothing pending review</td></tr>
                    )}
                    {reviewQueue.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100" data-testid={`pws-sm8-revenue-review-row-${item.id}`}>
                        <td className="px-3 py-2">{REVIEW_REASON_LABELS[item.reason] || item.reason}</td>
                        <td className="px-3 py-2">{item.job_address}</td>
                        <td className="px-3 py-2">{item.completion_date}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(item.total_invoice_amount)}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap justify-end">
                            {item.reason === "unresolved_region" && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => resolveReview(item.id, "assign_region", "pws_noosa")}
                                       data-testid={`pws-sm8-revenue-review-noosa-${item.id}`}>Noosa</Button>
                                <Button size="sm" variant="outline" onClick={() => resolveReview(item.id, "assign_region", "pws_maroochydore")}
                                       data-testid={`pws-sm8-revenue-review-maroochydore-${item.id}`}>Maroochydore</Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" title="Dismiss — excludes this job permanently" aria-label="Dismiss"
                                   onClick={() => resolveReview(item.id, "dismiss")}
                                   data-testid={`pws-sm8-revenue-review-dismiss-${item.id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Dismiss
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Demo tools */}
            <section className="rounded-xl border border-dashed border-slate-300 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Preview demo scenario</h3>
              <p className="text-xs text-slate-500 mb-3">
                Seeds the exact acceptance example from the brief (fake address, clearly flagged
                <code> is_test</code>): report published 1 Oct 2026 → 5 Oct/4 Nov/6 Dec jobs =
                15 jobs / $18,000, a January job excluded, one pending-invoice job, one
                excluded-region job. Never shown in the real numbers unless you toggle "Include
                demo/test data" above.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={busy}
                       onClick={() => runAction("Demo scenario seeded", () =>
                         api.post("/admin/pws-sm8-revenue/demo-seed"))}
                       data-testid="pws-sm8-revenue-demo-seed-btn">Seed demo scenario</Button>
                <Button size="sm" variant="ghost" disabled={busy}
                       onClick={() => runAction("Demo data cleared", () =>
                         api.post("/admin/pws-sm8-revenue/demo-clear"))}
                       data-testid="pws-sm8-revenue-demo-clear-btn">Clear demo data</Button>
              </div>
            </section>

            {/* Audit log */}
            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-3">Audit log</h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-64 overflow-y-auto">
                <table className="w-full text-sm" data-testid="pws-sm8-revenue-audit-log-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Action</th>
                      <th className="text-left px-3 py-2">By</th>
                      <th className="text-left px-3 py-2">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No admin actions yet</td></tr>
                    )}
                    {auditLog.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-xs text-slate-500">{fmtBrisbaneDateTime(row.at)}</td>
                        <td className="px-3 py-2">{row.action}</td>
                        <td className="px-3 py-2">{row.actor_email}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{JSON.stringify(row.details)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  const accentClass = accent === "emerald" ? "text-emerald-600" : accent === "amber" ? "text-amber-600" : "text-slate-800";
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`mt-2 text-2xl font-bold ${accentClass}`}>{value}</div>
    </div>
  );
}
